import React from "react";
import { motion } from "framer-motion";
import { Linkedin, Mail } from "lucide-react";
import { team } from "../mock";

const Team = () => {
  return (
    <section
      id="team"
      data-testid="team-section"
      className="relative py-24 md:py-32 bg-[#FDFDFD]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20"
        >
          <div className="lg:col-span-6">
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] font-semibold text-[#172554] mb-6">
              — Leadership
            </p>
            <h2
              data-testid="team-title"
              className="font-['Playfair_Display'] text-[#0A1128] text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-medium"
            >
              The partners
              <br />
              you actually <span className="italic">work with</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-[#475569] text-lg md:text-xl leading-relaxed font-['Outfit'] font-light">
              At Rampart Partners your engagement is managed by Senior Leadership
              who draws the needed resources and leads your team through all the
              steps to success.
            </p>
          </div>
        </motion.div>

        {/* Team grid — centered for 2 partners */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-12 md:gap-20 max-w-xl mx-auto">
          {team.map((m, i) => (
            <motion.div
              key={m.id}
              data-testid={`team-member-${m.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="group"
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-[#0A1128] mb-6">
                {m.image ? (
                  <img
                    src={m.image}
                    alt={m.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <Monogram initials={m.initials || initialsOf(m.name)} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                {/* Hover socials */}
                <div className="absolute bottom-4 left-4 flex gap-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                  <a
                    href={m.linkedin || "#"}
                    target={m.linkedin ? "_blank" : undefined}
                    rel={m.linkedin ? "noopener noreferrer" : undefined}
                    onClick={(e) => {
                      if (!m.linkedin) e.preventDefault();
                    }}
                    className="w-9 h-9 bg-white/90 backdrop-blur flex items-center justify-center hover:bg-[#CFA660] transition-colors"
                    aria-label={`${m.name} on LinkedIn`}
                    data-testid={`team-linkedin-${m.id}`}
                  >
                    <Linkedin className="w-4 h-4 text-[#0A1128]" />
                  </a>
                  <a
                    href="#contact"
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById("contact")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="w-9 h-9 bg-white/90 backdrop-blur flex items-center justify-center hover:bg-[#CFA660] transition-colors"
                    aria-label={`Email ${m.name}`}
                  >
                    <Mail className="w-4 h-4 text-[#0A1128]" />
                  </a>
                </div>
              </div>
              <div className="border-t border-[#0A1128]/15 pt-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-['Playfair_Display'] text-[#0A1128] text-xl md:text-2xl font-medium tracking-tight">
                      {m.name}
                    </h3>
                    <p className="mt-1 text-[#CFA660] text-xs uppercase tracking-[0.25em] font-semibold">
                      {m.role}
                    </p>
                  </div>
                  {m.linkedin && (
                    <a
                      href={m.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`team-linkedin-${m.id}`}
                      aria-label={`${m.name} on LinkedIn`}
                      className="flex-shrink-0 w-9 h-9 border border-[#0A1128]/30 flex items-center justify-center hover:bg-[#0A1128] hover:border-[#0A1128] group/li transition-colors"
                    >
                      <Linkedin className="w-4 h-4 text-[#0A1128] group-hover/li:text-[#CFA660] transition-colors" />
                    </a>
                  )}
                </div>
                {m.bio && (
                  <p className="mt-4 text-[#475569] text-sm leading-relaxed font-['Outfit']">
                    {m.bio}
                  </p>
                )}
                {m.focus && (
                  <p className="mt-4 text-[#0A1128] text-xs uppercase tracking-wider font-['Outfit']">
                    Focus · <span className="text-[#475569] normal-case tracking-normal">{m.focus}</span>
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Monogram = ({ initials }) => (
  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#0A1128] via-[#0A1128] to-[#172554]">
    {/* Subtle radial accent */}
    <div
      className="absolute inset-0 opacity-30"
      style={{
        background:
          "radial-gradient(circle at 30% 20%, rgba(207,166,96,0.35), transparent 55%)",
      }}
    />
    {/* Grain */}
    <div
      className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
      }}
    />
    <div className="relative flex flex-col items-center">
      <span className="font-['Playfair_Display'] text-[#CFA660] text-7xl md:text-8xl italic font-medium tracking-tight">
        {initials}
      </span>
      <span className="mt-4 text-white/60 text-[10px] uppercase tracking-[0.4em]">
        Rampart Partners
      </span>
    </div>
  </div>
);

const initialsOf = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();

export default Team;
